import React, { useEffect, useRef } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import gsap from "gsap";
import { setupBecomePartnerAnimation } from "../animations";
import "./partners.css";
import { colors } from "../../../../theme/colors";
import { layoutSizes } from "../../../../theme/index.js";

const BecomePartner = () => {
  const ctaRef = useRef(null);
  const ctaContentRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const cta = ctaRef.current;
    const ctaContent = ctaContentRef.current;

    if (!cta) return;

    const ctx = gsap.context(() => {
      setupBecomePartnerAnimation(cta);
    }, cta);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <Box ref={ctaRef} className="partners-cta" sx={{
      position: "relative",
      width: '100%',
      minHeight: { xs: '100px', sm: '150px', md: '160px' },
      margin: "0 auto",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      borderRadius: "12px",
      background: `
        radial-gradient(circle at 85% 30%, ${colors.alpha.red[30]}, transparent 32%),
        radial-gradient(circle at 20% 80%, ${colors.alpha.primary[28]}, transparent 34%),
        ${colors.gradients.becomePartner}
      `,
      backgroundSize: "180% 180%",
      boxShadow: `0 25px 70px ${colors.alpha.navyDeep[18]}`,
      willChange: "transform",
      '&::before': {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `linear-gradient(110deg, transparent 30%, ${colors.alpha.white[12]} 50%, transparent 70%)`,
        backgroundSize: "200% 100%",
        animation: "partnersShine 7s linear infinite",
        pointerEvents: "none",
      }
    }}>
      <Box className="partners-glow partners-glow-one" sx={{
        position: "absolute",
        borderRadius: "50%",
        filter: "blur(5px)",
        pointerEvents: "none",
        width: { xs: '120px', md: '240px' },
        height: { xs: '120px', md: '240px' },
        right: { xs: '-40px', md: '8%' },
        top: { xs: '-40px', md: '-100px' },
        background: colors.alpha.red[15]
      }} />
      <Box className="partners-glow partners-glow-two" sx={{
        position: "absolute",
        borderRadius: "50%",
        filter: "blur(5px)",
        pointerEvents: "none",
        width: { xs: '100px', md: '220px' },
        height: { xs: '100px', md: '220px' },
        right: { xs: '20%', md: '25%' },
        bottom: { xs: '-60px', md: '-150px' },
        background: colors.alpha.primary[18]
      }} />

      <Box ref={ctaContentRef} className="partners-cta-content" sx={{
        position: "relative",
        zIndex: 3,
        padding: { xs: '16px', sm: layoutSizes.homeCardPadding },
      }}>
        <Typography className="partners-cta-title" sx={{
          maxWidth: '700px',
          color: colors.neutral.white,
          fontSize: { xs: '20px', sm: '22px', md: '26px' },
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: { xs: '-0.5px', md: '-1px' },
          mb: { xs: '4px', sm: '8px' },
        }}>
          Be Part of the <Box component="span" sx={{ color: colors.red[500], fontSize: 'inherit', fontWeight: 'inherit', fontFamily: 'inherit' }}>AutoBET</Box> Revolution
        </Typography>

        <Typography className="partners-cta-description" sx={{
          color: colors.slate[300],
          fontSize: { xs: '10px', sm: '11px', md: '13px' },
          lineHeight: { xs: 1.3, sm: 1.5 },
          mb: { xs: '10px', md: '18px' },
        }}>
          Play, earn, and help shape the future of decentralized gaming.
        </Typography>

        <Box className="partners-cta-actions" sx={{
          display: "flex",
          alignItems: { xs: 'center', sm: 'center' },
          flexDirection: { xs: 'row', sm: 'row' },
          gap: { xs: '8px', md: '14px' },
        }}>
          <Button variant="contained" className="partners-primary-button" sx={{
            minWidth: { xs: '90px', sm: '100px' },
            height: { xs: '32px', sm: '36px' },
            padding: { xs: '0 12px', sm: '0 16px' },
            borderRadius: '8px',
            background: colors.red[500],
            color: colors.neutral.white,
            fontSize: { xs: '10px', sm: '12px' },
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'transform .3s ease, box-shadow .3s ease, background .3s ease',
            '&:hover': {
              background: colors.red[600],
              transform: 'translateY(-3px)',
              boxShadow: `0 12px 25px ${colors.alpha.red[30]}`,
            }
          }}>
            Play Now
          </Button>

          <Button variant="outlined" className="partners-secondary-button" sx={{
            minWidth: { xs: '100px', sm: '120px' },
            height: { xs: '32px', sm: '36px' },
            padding: { xs: '0 12px', sm: '0 16px' },
            borderRadius: '8px',
            border: `1px solid ${colors.alpha.white[50]}`,
            color: colors.neutral.white,
            fontSize: { xs: '10px', sm: '12px' },
            fontWeight: 600,
            textTransform: 'none',
            transition: 'transform .3s ease, background .3s ease, border-color .3s ease',
            '&:hover': {
              background: colors.alpha.white[12],
              borderColor: colors.neutral.white,
              transform: 'translateY(-3px)',
            }
          }}>
            Join Community
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BecomePartner;
